import { useState } from 'react';
import { profileOrders } from '@/mocks/profile';

const STATUS_CONFIG = {
  delivered: { color: '#39ff14', icon: 'ri-check-double-line', label: 'Доставлен' },
  cancelled: { color: '#ff2d78', icon: 'ri-close-circle-line', label: 'Отменён' },
  processing: { color: '#f0ff00', icon: 'ri-loader-4-line', label: 'В обработке' },
  shipping: { color: '#00f5ff', icon: 'ri-truck-line', label: 'В пути' },
};

const ProfileOrders = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'delivered' | 'cancelled'>('all');

  const filtered = filter === 'all' ? profileOrders : profileOrders.filter(o => o.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-orbitron font-black text-xl text-white">МОИ ЗАКАЗЫ</h2>
        <div className="flex gap-1 p-1 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {[{ id: 'all', label: 'Все' }, { id: 'delivered', label: 'Доставлены' }, { id: 'cancelled', label: 'Отменены' }].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id as typeof filter)}
              className="whitespace-nowrap px-4 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
              style={filter === f.id ? { background: '#ff2d78', color: '#fff', boxShadow: '0 0 10px rgba(255,45,120,0.4)' } : { color: 'rgba(255,255,255,0.4)' }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filtered.map(order => {
          const cfg = STATUS_CONFIG[order.status as keyof typeof STATUS_CONFIG];
          const isExpanded = expandedId === order.id;
          return (
            <div
              key={order.id}
              className="rounded-2xl overflow-hidden transition-all duration-300"
              style={{ border: `1px solid ${isExpanded ? cfg.color + '30' : 'rgba(255,255,255,0.06)'}`, background: isExpanded ? `${cfg.color}04` : 'rgba(255,255,255,0.02)' }}
            >
              {/* Order header */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : order.id)}
                className="w-full flex items-center gap-4 px-5 py-4 cursor-pointer text-left"
              >
                {/* Status icon */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}30` }}>
                  <i className={cfg.icon} style={{ color: cfg.color, fontSize: '18px' }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-orbitron font-bold text-sm text-white">{order.id}</span>
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                      {cfg.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="font-mono text-xs text-white/35">{order.date}</span>
                    <span className="font-mono text-xs text-white/25">·</span>
                    <span className="font-mono text-xs text-white/35">{order.items.length} {order.items.length === 1 ? 'товар' : 'товара'}</span>
                    <span className="font-mono text-xs text-white/25">·</span>
                    <span className="font-mono text-xs text-white/35">{order.deliveryType}</span>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <div className="font-orbitron font-bold text-lg text-white">{order.total.toLocaleString()} ₽</div>
                  {order.bonusEarned > 0 && (
                    <div className="font-mono text-[10px]" style={{ color: '#39ff14' }}>+{order.bonusEarned} баллов</div>
                  )}
                </div>

                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  {isExpanded
                    ? <i className="ri-arrow-up-s-line text-white/30" />
                    : <i className="ri-arrow-down-s-line text-white/30" />
                  }
                </div>
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div className="px-5 pb-5" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="pt-4 space-y-3 mb-4">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <img src={item.image} alt={item.name} className="w-10 h-10 object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs text-white/80 leading-snug line-clamp-2">{item.name}</div>
                          <div className="font-mono text-[11px] text-white/35 mt-0.5">{item.qty} шт. × {item.price.toLocaleString()} ₽</div>
                        </div>
                        <div className="font-orbitron font-bold text-sm text-white shrink-0">{(item.qty * item.price).toLocaleString()} ₽</div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 flex-wrap gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-2 font-mono text-xs text-white/35">
                      <i className="ri-map-pin-line" />
                      {order.address}
                    </div>
                    <div className="flex gap-2">
                      {order.status === 'delivered' && (
                        <button className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200" style={{ border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}>
                          <i className="ri-refresh-line" />
                          Повторить заказ
                        </button>
                      )}
                      <button className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200" style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }}>
                        <i className="ri-file-list-line" />
                        Детали
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProfileOrders;
